import React, { useEffect, useState } from "react";
import { Loader2, UserPlus, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const ROLE_LABEL = { admin: "Admin", processor: "Loan Processor", loan_officer: "Loan Officer", borrower: "Remove access" };
const inputCls = "bg-surface/70 border-edge text-white h-11";
const selectCls = "h-11 rounded-md bg-surface/70 border border-edge text-white px-3 text-sm";

function InviteForm({ onDone }) {
  const [f, setF] = useState({ name: "", email: "", role: "processor" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!f.name || !f.email) { toast.error("Name and email are required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/staff/invite", f);
      toast.success(data.email_sent ? `Invite emailed to ${f.email}` : "Account created, but the email could not be sent — try Resend");
      setF({ name: "", email: "", role: "processor" });
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="card-surface rounded-2xl p-6" data-testid="team-invite-form">
      <div className="eyebrow mb-3">Invite a Team Member</div>
      <div className="grid sm:grid-cols-[1fr_1fr_180px_auto] gap-2">
        <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Full name" className={inputCls} data-testid="team-invite-name" />
        <Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="email@company.com" type="email" className={inputCls} data-testid="team-invite-email" />
        <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className={selectCls} data-testid="team-invite-role">
          <option value="processor">Loan Processor</option>
          <option value="loan_officer">Loan Officer</option>
        </select>
        <Button onClick={send} disabled={busy} className="btn-gold rounded-lg h-11 px-5 gap-2" data-testid="team-invite-send">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4" /> Send Invite</>}
        </Button>
      </div>
      <p className="text-xs text-faint mt-2">Processors only see files assigned to them (including deals they take by phone). Loan officers see every file.</p>
    </div>
  );
}

function MemberRow({ u, onChanged }) {
  const resend = async () => {
    try { const { data } = await api.post(`/admin/staff/${u.user_id}/resend-invite`); toast.success(data.email_sent ? "Invite re-sent" : "Email could not be sent"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const setRole = async (role) => {
    if (role === "borrower" && !window.confirm(`Remove ${u.email}'s team access?`)) return;
    try { await api.put("/admin/staff/role", { email: u.email, role }); toast.success("Role updated"); onChanged(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-between border-b border-edge/60 py-3" data-testid={`team-member-${u.email}`}>
      <div className="min-w-0">
        <div className="text-white font-semibold truncate">{u.name || u.email}
          {u.invite_pending && <span className="ml-2 text-[10px] uppercase tracking-wider text-warn border border-warn/40 rounded px-1.5 py-0.5" data-testid={`team-pending-${u.email}`}>Invite pending</span>}
        </div>
        <div className="text-xs text-faint truncate">{u.email} · {u.files} open file{u.files === 1 ? "" : "s"}</div>
      </div>
      <div className="flex gap-2">
        {u.invite_pending && <Button onClick={resend} variant="outline" className="btn-outline-glass rounded-lg h-9 gap-1 text-xs" data-testid={`team-resend-${u.email}`}><Mail className="w-3.5 h-3.5" /> Resend</Button>}
        <select value={u.role} onChange={(e) => setRole(e.target.value)} className={`${selectCls} h-9`} data-testid={`team-role-${u.email}`}>
          {Object.entries(ROLE_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
    </div>
  );
}

export default function AdminTeam() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/staff").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  return (
    <div data-testid="admin-team" className="max-w-4xl">
      <h1 className="h-display text-3xl text-white mb-6">Team</h1>
      <InviteForm onDone={load} />
      <div className="card-surface rounded-2xl p-6 mt-6">
        <div className="eyebrow mb-2">Team Members</div>
        {rows === null ? <Loader2 className="w-6 h-6 text-gold animate-spin" /> : rows.map((u) => <MemberRow key={u.user_id} u={u} onChanged={load} />)}
      </div>
    </div>
  );
}
